"use client";

import { useCallback, useMemo } from "react";
import { useGlobalModalStack } from "@west-tennessee-consulting/ui";
import { toast } from "sonner";
import type { BaseEntity, SerializedViewState } from "../../UniversalCRMTable/types";
import { getSupabase } from "@/lib/hooks/api/utils";
import type { EmailRecipient } from "@/components/crm/messages/ClientEmailSelector";

/**
 * Composes bulk action handlers using current filters and GlobalModalStack
 *
 * This hook creates the bulk action utilities that are stored in UniversalEntityPageContext.
 * It handles:
 * - Converting SerializedViewState (currentFilters) to filter criteria for API calls
 * - Managing modal display for bulk action results
 * - Executing bulk operations (email export, etc.)
 *
 * CRITICAL: Uses currentFilters (SerializedViewState) which represents the actual applied filters,
 * NOT FilterContext.filters.simple which is just UI state.
 */
export function useBulkActionsComposer<T extends BaseEntity>(
  entityName: string,
  currentFilters: SerializedViewState,
  bulkActionMutations?: { emailExport?: any },
  setBulkEmailExportData?: (data: { emailList: string; totalCount: number } | null) => void
) {
  const globalModalStack = useGlobalModalStack();

  const buildRecipientsFromSelectedItems = useCallback(
    async (selectedItems: T[]): Promise<EmailRecipient[]> => {
      const deduped = new Map<string, EmailRecipient>();

      const addRecipient = (emailValue: unknown, nameValue?: unknown) => {
        if (typeof emailValue !== "string") return;
        const email = emailValue.trim();
        if (!email || deduped.has(email)) return;
        deduped.set(email, {
          id: `email-${email}`,
          type: "email",
          email,
          name: typeof nameValue === "string" && nameValue.trim() ? nameValue.trim() : email,
        });
      };

      selectedItems.forEach((item) => {
        const row = item as Record<string, unknown>;
        addRecipient(row.email ?? row.contact_email, row.name ?? row.contact_name);
      });

      if (entityName === "companies") {
        const companyIds = Array.from(
          new Set(
            selectedItems
              .map((item) => item.id)
              .filter((id): id is string => typeof id === "string" && id.length > 0)
          )
        );

        if (companyIds.length) {
          try {
            const supabase = getSupabase();
            const { data, error } = await supabase
              .from("contacts")
              .select("id, name, email")
              .in("company_id", companyIds)
              .not("email", "is", null)
              .limit(5000);

            if (error) {
              console.error("Failed to fetch company contacts for bulk compose:", error);
            } else if (data?.length) {
              data.forEach((contact) => addRecipient(contact.email, contact.name));
            }
          } catch (error) {
            console.error("Error fetching company contacts for bulk compose:", error);
          }
        }
      }

      return Array.from(deduped.values());
    },
    [entityName]
  );

  // Show bulk action result in modal
  // Note: This function is called by the bulk action handler
  // The actual data storage happens in the handler itself
  const showBulkActionResult = useCallback(
    (data: { emailList: string; totalCount: number }) => {
      // Store result in context state instead of sessionStorage
      if (setBulkEmailExportData) {
        setBulkEmailExportData(data);
      }

      // Open the appropriate modal based on entity type
      let modalId = "bulk-export-emails-contacts";
      if (entityName === "companies") {
        modalId = "bulk-export-emails-companies";
      } else if (entityName === "deals") {
        modalId = "bulk-export-emails-deals";
      }

      globalModalStack.openModal(modalId);
    },
    [entityName, globalModalStack, setBulkEmailExportData]
  );

  // Handle bulk export emails
  const handleBulkExportEmails = useCallback(async (selectedItems?: T[]) => {
    console.log(
      "[BULK_ACTION] handleBulkExportEmails called, entityName:",
      entityName,
      "hasMutation:",
      !!bulkActionMutations?.emailExport
    );

    if (selectedItems?.length) {
      const recipients = await buildRecipientsFromSelectedItems(selectedItems);
      if (!recipients.length) {
        toast.error("No email addresses found for selected items");
        return;
      }

      globalModalStack.openModal("compose-message", {
        initialRecipients: recipients,
      });
      return;
    }

    if (!bulkActionMutations?.emailExport) {
      toast.error("Email export mutation not available");
      console.warn("Email export mutation not available");
      return;
    }

    try {
      // Convert SerializedViewState to filter criteria based on entity type
      let filterCriteria: Record<string, any> = {};

      if (entityName === "contacts") {
        // Extract filters from SerializedViewState
        // currentFilters has structure: { filters: {...}, advancedFilters: {...}, search: "..." }
        const filters = currentFilters.filters || {};
        const advancedFilters = currentFilters.advancedFilters || {};
        const search = currentFilters.search || "";

        console.log("[BULK_ACTION] extracted filters:", filters);
        console.log("[BULK_ACTION] extracted advancedFilters:", advancedFilters);
        console.log("[BULK_ACTION] extracted search:", search);

        // Build filter criteria - pass all filters directly to the mutation
        // The mutation will handle mapping them to the appropriate query fields
        filterCriteria = {
          ...filters, // Spread all simple filters (status, tags, company_name, etc.)
          nameSearch: filters.name || search || undefined,
          companySearch: filters.company_name || undefined,
          emailSearch: filters.email || undefined,
          advancedFilters: Object.keys(advancedFilters).length > 0 ? advancedFilters : undefined,
          search: search || undefined,
        };
      } else if (entityName === "companies") {
        const filters = currentFilters.filters || {};
        const advancedFilters = currentFilters.advancedFilters || {};
        const search = currentFilters.search || "";

        filterCriteria = {
          nameSearch: filters.name || search || undefined,
          industrySearch: filters.industry || undefined,
          statusSearch: filters.status || undefined,
          advancedFilters: Object.keys(advancedFilters).length > 0 ? advancedFilters : undefined,
          search: search || undefined,
        };
      } else if (entityName === "deals") {
        const filters = currentFilters.filters || {};
        const advancedFilters = currentFilters.advancedFilters || {};
        const search = currentFilters.search || "";

        filterCriteria = {
          nameSearch: filters.name || search || undefined,
          companySearch: filters.company_name || undefined,
          statusSearch: filters.status || undefined,
          stageSearch: filters.stage || undefined,
          advancedFilters: Object.keys(advancedFilters).length > 0 ? advancedFilters : undefined,
          search: search || undefined,
        };
      }

      console.log("[BULK_ACTION] filter criteria built:", filterCriteria);

      toast.loading("Exporting emails...");

      const result = await bulkActionMutations.emailExport.mutateAsync({
        filterCriteria,
      });

      console.log("[BULK_ACTION] mutation result:", result);

      // Show result in modal
      showBulkActionResult({
        emailList: result.emailList,
        totalCount: result.totalCount,
      });
    } catch (error) {
      console.error("Error exporting emails:", error);
      toast.error(error instanceof Error ? error.message : "Failed to export emails");
    }
  }, [
    currentFilters,
    bulkActionMutations,
    showBulkActionResult,
    entityName,
    buildRecipientsFromSelectedItems,
    globalModalStack,
  ]);

  return {
    currentFilters,
    showBulkActionResult,
    handleBulkExportEmails,
  };
}
